import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { TransactionAllocation } from "@/lib/api";
import {
  ArrowLeft,
  Banknote,
  Loader2,
  Plus,
  Trash2,
  Check,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-AU", {
    style: "currency",
    currency: "AUD",
  });

const Field = ({ label, children }) => (
  <label className="block">
    <div className="swiss-label mb-1">{label}</div>
    {children}
  </label>
);

const AllocateTransaction = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const initialTx = state?.tx || {};

  const [amount, setAmount] = useState(String(initialTx.amount ?? ""));
  const [txDate, setTxDate] = useState(initialTx.transaction_date || new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState(initialTx.reference || "");
  const [description, setDescription] = useState(initialTx.description || "");
  const [counterparty, setCounterparty] = useState(initialTx.counterparty || "");

  const [matching, setMatching] = useState(false);
  const [results, setResults] = useState(null); // {bills, best_match_bill_id, ...}
  const [lines, setLines] = useState([]); // [{bill_id, amount, note}]
  const [saving, setSaving] = useState(false);

  const total = Number(amount) || 0;
  const allocated = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const remaining = Math.max(0, total - allocated);
  const over = allocated > total + 0.005;

  const runMatch = async () => {
    if (!total || total <= 0) {
      toast.error("Enter the transaction amount first");
      return;
    }
    setMatching(true);
    try {
      const r = await TransactionAllocation.match(total, reference, counterparty);
      setResults(r);
      if (r.best_match_bill_id) {
        const best = r.bills.find((b) => b.id === r.best_match_bill_id);
        setLines([{ bill_id: best.id, amount: Math.min(total, best.remaining_balance).toFixed(2), note: "" }]);
        toast.success(`Best match: ${best.company} (${Math.round(best.match_score * 100)}% confident)`);
      } else {
        setLines([]);
        toast.message("No confident match — pick a bill below.");
      }
    } catch (e) {
      toast.error("Match failed. Try again.");
    } finally {
      setMatching(false);
    }
  };

  // Auto-match on mount when we have an amount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (total > 0) runMatch(); }, []);

  const addLine = () => {
    if (!results?.bills?.length) return;
    // pick the next not-already-added bill
    const used = new Set(lines.map((l) => l.bill_id));
    const next = results.bills.find((b) => !used.has(b.id));
    if (!next) return toast.message("All visible bills already added");
    setLines([...lines, { bill_id: next.id, amount: remaining.toFixed(2), note: "" }]);
  };

  const updateLine = (i, patch) => {
    const copy = [...lines];
    copy[i] = { ...copy[i], ...patch };
    setLines(copy);
  };

  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const save = async () => {
    if (lines.length === 0) return toast.error("Add at least one allocation");
    if (lines.some((l) => !Number(l.amount) || Number(l.amount) <= 0))
      return toast.error("Each allocation must have an amount > 0");
    if (over) return toast.error("Allocated more than the transaction total");

    setSaving(true);
    try {
      await TransactionAllocation.splitAllocate({
        transaction_date: txDate || null,
        transaction_reference: reference || null,
        method: "EFT",
        allocations: lines.map((l) => ({
          bill_id: l.bill_id,
          amount: Number(l.amount),
          note: l.note || null,
        })),
      });
      toast.success(
        lines.length === 1
          ? "Payment allocated."
          : `Payment split across ${lines.length} bills.`,
      );
      navigate("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not allocate. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const billById = useMemo(() => {
    const map = {};
    (results?.bills || []).forEach((b) => (map[b.id] = b));
    return map;
  }, [results]);

  return (
    <div data-testid="allocate-page" className="fade-rise">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-500 hover:text-black mb-6"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="swiss-label mb-3 inline-flex items-center gap-2">
        <Banknote className="h-3 w-3" /> Bank Transaction
      </div>
      <h1 className="font-display font-black text-4xl tracking-tight leading-tight">
        Allocate to a bill
      </h1>
      <p className="text-neutral-600 mt-3 max-w-2xl">
        Tell Billy what came out of the bank, then allocate it to one or more
        outstanding bills. Split it across multiple bills if you need to.
      </p>

      <div className="mt-3">
        <button
          onClick={() => navigate("/bank-statement")}
          data-testid="open-bank-statement"
          className="text-[11px] uppercase tracking-widest font-bold text-neutral-600 hover:text-black underline underline-offset-4 decoration-2"
        >
          Got a whole month's statement? Reconcile it →
        </button>
      </div>

      {/* Transaction details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8 border-2 border-black p-5">
        <Field label="Amount">
          <Input
            data-testid="input-amount"
            className="swiss-input mono"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Date">
          <Input
            data-testid="input-date"
            type="date"
            className="swiss-input"
            value={txDate}
            onChange={(e) => setTxDate(e.target.value)}
          />
        </Field>
        <Field label="Reference">
          <Input
            data-testid="input-reference"
            className="swiss-input"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="BPAY CRN, invoice #, etc."
          />
        </Field>
        <Field label="Counterparty">
          <Input
            data-testid="input-counterparty"
            className="swiss-input"
            value={counterparty}
            onChange={(e) => setCounterparty(e.target.value)}
            placeholder="Vendor name"
          />
        </Field>
        <Field label="Description">
          <Input
            data-testid="input-description"
            className="swiss-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <div className="flex items-end">
          <button
            onClick={runMatch}
            data-testid="rematch-btn"
            disabled={matching}
            className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-4 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
          >
            {matching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Find matches
          </button>
        </div>
      </div>

      {/* Allocation lines */}
      {results && (
        <div className="mt-8">
          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="swiss-label">Allocate to</div>
              <div className="font-display font-black text-2xl mt-1">
                {lines.length} bill{lines.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="text-right">
              <div className="swiss-label">Remaining</div>
              <div
                className={`font-display font-black text-2xl mono mt-1 ${over ? "text-red-600" : remaining > 0.005 ? "text-amber-600" : "text-green-600"}`}
              >
                {fmt(remaining)}
              </div>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            {lines.map((line, i) => {
              const bill = billById[line.bill_id];
              return (
                <div
                  key={i}
                  data-testid={`alloc-line-${i}`}
                  className="border-2 border-black p-4 bg-white"
                >
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_140px_auto] gap-3 items-end">
                    <Field label={`Bill #${i + 1}`}>
                      <select
                        data-testid={`alloc-bill-${i}`}
                        className="swiss-input w-full appearance-none cursor-pointer"
                        value={line.bill_id}
                        onChange={(e) => updateLine(i, { bill_id: e.target.value })}
                      >
                        {(results.bills || []).map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.match_score >= 0.5 ? "★ " : ""}
                            {b.company} · {b.invoice_number} · {fmt(b.remaining_balance)} owing
                          </option>
                        ))}
                      </select>
                      {bill && (
                        <div className="text-[11px] text-neutral-500 mt-1">
                          Due {bill.due_date} · status {bill.status}
                        </div>
                      )}
                    </Field>
                    <Field label="Amount">
                      <Input
                        data-testid={`alloc-amount-${i}`}
                        className="swiss-input mono"
                        inputMode="decimal"
                        value={line.amount}
                        onChange={(e) => updateLine(i, { amount: e.target.value })}
                      />
                    </Field>
                    <button
                      onClick={() => removeLine(i)}
                      data-testid={`alloc-remove-${i}`}
                      className="border-2 border-black bg-white text-black p-3 hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
                      aria-label="Remove allocation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}

            <button
              onClick={addLine}
              data-testid="add-line-btn"
              className="w-full border-2 border-dashed border-black bg-white text-black font-bold uppercase tracking-widest text-[11px] px-4 py-4 hover:bg-black hover:text-white transition-colors inline-flex items-center justify-center gap-2"
            >
              <Plus className="h-4 w-4" /> Add another bill
            </button>
          </div>

          {/* Save */}
          <div className="mt-8 flex flex-wrap gap-3 items-center">
            <button
              data-testid="save-allocation-btn"
              onClick={save}
              disabled={saving || lines.length === 0 || over}
              className="border-2 border-black bg-black text-white font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-white hover:text-black transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {lines.length > 1 ? `Split ${fmt(allocated)} across ${lines.length} bills` : `Allocate ${fmt(allocated)}`}
            </button>
            <button
              onClick={() => navigate("/new")}
              data-testid="add-bill-btn"
              className="text-[11px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
            >
              Save as new Bill instead
            </button>
            <button
              onClick={() => navigate("/receipts/new")}
              data-testid="add-receipt-btn"
              className="text-[11px] uppercase tracking-widest font-bold text-neutral-500 hover:text-black"
            >
              Save as Receipt instead
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllocateTransaction;
